/* @jsx h */
import { computed, defineComponent, h, inject, provide, ref, type InjectionKey, type Ref, type VNode } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CampaignOffer, SampleCustomer, StudioCampaign, StudioChannel } from "./types";
import { SAMPLE_CUSTOMERS, STUB_PRODUCTS, STUB_SHOP } from "./stub";
import { TEMPLATE_VARIABLES, renderTemplate, variableValues } from "./variables";

export const formatMoney = (cents: number) =>
  new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(cents / 100);

export const formatShortDate = (date: Date) =>
  date.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });

export const productName = (id: string | null) =>
  STUB_PRODUCTS.find((p) => p.id === id)?.name ?? "—";

export const CHANNEL_META: Record<StudioChannel, { icon: string; label: string; tone: string }> = {
  email: { icon: "mail", label: "campaigns.studio.channel_email", tone: "text-info" },
  whatsapp: { icon: "chat", label: "campaigns.studio.channel_whatsapp", tone: "text-success" },
  sms: { icon: "sms", label: "campaigns.studio.channel_sms", tone: "text-warning" },
  push: { icon: "wallet", label: "campaigns.studio.channel_push", tone: "text-primary" },
};

export const useOfferText = () => {
  const { t } = useLocalization();
  return (offer: CampaignOffer) => {
    if (offer.type === "none") return "";
    switch (offer.reward) {
      case "percentage":
        return t("campaigns.studio_offer.text_percentage", [offer.value]);
      case "fixed":
        return t("campaigns.studio_offer.text_fixed", [formatMoney(offer.value * 100)]);
      case "free_item":
        return t("campaigns.studio_offer.text_free_item", [productName(offer.product_id)]);
    }
  };
};

// ----------------------------
// Preview context: the sample customer every channel preview renders for.
// ----------------------------

type PreviewContext = {
  customer: Ref<SampleCustomer>;
  showRaw: Ref<boolean>;
  render: (template: string) => string;
};

// Symbol.for keeps the key stable across HMR reloads of this module.
const PreviewKey: InjectionKey<PreviewContext> = Symbol.for("campaign-studio-preview");

export const providePreview = (campaign: Ref<StudioCampaign>) => {
  const customer = ref<SampleCustomer>(SAMPLE_CUSTOMERS[0]);
  const showRaw = ref(false);
  const offerText = useOfferText();
  const values = computed(() =>
    variableValues({
      customer: customer.value,
      offer: campaign.value.offer,
      offerText: offerText(campaign.value.offer),
      shop: STUB_SHOP,
      formatDate: formatShortDate,
    }),
  );
  const ctx: PreviewContext = {
    customer,
    showRaw,
    render: (template) => (showRaw.value ? template : renderTemplate(template, values.value)),
  };
  provide(PreviewKey, ctx);
  return ctx;
};

export const usePreview = () => {
  const ctx = inject(PreviewKey);
  if (!ctx) throw new Error("usePreview must be used inside the campaign builder");
  return ctx;
};

export const SampleCustomerPicker = defineComponent({
  name: "SampleCustomerPicker",
  setup() {
    const { t } = useLocalization();
    const preview = usePreview();
    return () => (
      <div class="flex flex-wrap items-center gap-2 text-sm">
        <span class="text-base-content/60">{t("campaigns.studio.preview_as")}</span>
        <select
          class="select select-bordered select-sm"
          value={preview.customer.value.id}
          onChange={(e) => {
            const id = (e.target as HTMLSelectElement).value;
            preview.customer.value = SAMPLE_CUSTOMERS.find((c) => c.id === id) ?? SAMPLE_CUSTOMERS[0];
          }}
        >
          {SAMPLE_CUSTOMERS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.first_name} {c.last_name}
            </option>
          ))}
        </select>
        <label class="label cursor-pointer gap-2 py-0">
          <input
            type="checkbox"
            class="toggle toggle-sm"
            checked={preview.showRaw.value}
            onChange={(e) => (preview.showRaw.value = (e.target as HTMLInputElement).checked)}
          />
          <span class="label-text">{t("campaigns.studio.show_placeholders")}</span>
        </label>
      </div>
    );
  },
});

// ----------------------------
// Form building blocks
// ----------------------------

type FieldProps = { label: string; hint?: string; counter?: string; error?: boolean };

export const Field = defineComponent<FieldProps>({
  name: "StudioField",
  props: ["label", "hint", "counter", "error"],
  setup(props, { slots }) {
    return () => (
      <label class="form-control w-full">
        <div class="label pb-1">
          <span class="label-text font-medium">{props.label}</span>
          {props.counter && (
            <span class={["label-text-alt tabular-nums", props.error ? "text-error" : "text-base-content/50"]}>
              {props.counter}
            </span>
          )}
        </div>
        {slots.default?.()}
        {props.hint && (
          <div class="label pt-1">
            <span class="label-text-alt text-base-content/50">{props.hint}</span>
          </div>
        )}
      </label>
    );
  },
});

type SegmentedProps = {
  value: string;
  options: { value: string; label: string; icon?: string }[];
  onChange: (value: string) => void;
  size?: "sm" | "xs";
};

export const Segmented = defineComponent<SegmentedProps>({
  name: "StudioSegmented",
  props: ["value", "options", "onChange", "size"],
  setup(props) {
    return () => (
      <div class="join w-full">
        {props.options.map((o) => (
          <button
            key={o.value}
            type="button"
            class={[
              "btn join-item flex-1 gap-1",
              props.size === "xs" ? "btn-xs" : "btn-sm",
              props.value === o.value ? "btn-primary" : "btn-ghost border-base-300",
            ]}
            onClick={() => props.onChange(o.value)}
          >
            {o.icon && <span class="material-symbols-outlined text-base">{o.icon}</span>}
            {o.label}
          </button>
        ))}
      </div>
    );
  },
});

type VariableChipsProps = { channel: StudioChannel; onInsert: (token: string) => void };

export const VariableChips = defineComponent<VariableChipsProps>({
  name: "StudioVariableChips",
  props: ["channel", "onInsert"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="flex flex-wrap gap-1">
        {TEMPLATE_VARIABLES.filter((v) => v.channels.includes(props.channel)).map((v) => (
          <button
            key={v.key}
            type="button"
            class="badge badge-outline gap-1 hover:badge-primary"
            onMousedown={(e) => e.preventDefault()}
            onClick={() => props.onInsert(`{{${v.key}}}`)}
          >
            <span class="material-symbols-outlined text-xs">add</span>
            {t(v.label)}
          </button>
        ))}
      </div>
    );
  },
});

// Inserts `token` at the caret of the textarea/input `el`, falling back to append.
export const insertAtCursor = (
  el: HTMLInputElement | HTMLTextAreaElement | null | undefined,
  current: string,
  token: string,
): string => {
  if (!el || el.selectionStart == null) return current + token;
  const start = el.selectionStart;
  const end = el.selectionEnd ?? start;
  const next = current.slice(0, start) + token + current.slice(end);
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(start + token.length, start + token.length);
  });
  return next;
};

// A textarea with variable chips that inserts at the caret.
type TemplateTextareaProps = {
  value: string;
  channel: StudioChannel;
  rows?: number;
  maxLength?: number;
  placeholder?: string;
  onChange: (value: string) => void;
  toolbar?: () => VNode;
};

export const TemplateTextarea = defineComponent<TemplateTextareaProps>({
  name: "StudioTemplateTextarea",
  props: ["value", "channel", "rows", "maxLength", "placeholder", "onChange", "toolbar"],
  setup(props, { expose }) {
    const el = ref<HTMLTextAreaElement>();
    const insert = (token: string) => props.onChange(insertAtCursor(el.value, props.value, token));
    const wrap = (marker: string) => {
      const ta = el.value;
      if (!ta) return;
      const { selectionStart: s, selectionEnd: e } = ta;
      const v = props.value;
      props.onChange(v.slice(0, s) + marker + v.slice(s, e) + marker + v.slice(e));
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(s + marker.length, e + marker.length);
      });
    };
    expose({ insert, wrap });
    return () => (
      <div class="space-y-2">
        <div class="overflow-hidden rounded-lg border border-base-300 focus-within:border-primary">
          {props.toolbar && <div class="flex gap-1 border-b border-base-300 bg-base-200 px-2 py-1">{props.toolbar()}</div>}
          <textarea
            ref={el}
            class="textarea w-full resize-y rounded-none border-0 leading-relaxed focus:outline-none"
            rows={props.rows ?? 5}
            maxlength={props.maxLength}
            placeholder={props.placeholder}
            value={props.value}
            onInput={(e) => props.onChange((e.target as HTMLTextAreaElement).value)}
          />
        </div>
        <VariableChips channel={props.channel} onInsert={insert} />
      </div>
    );
  },
});

type TemplateInputProps = {
  value: string;
  channel: StudioChannel;
  maxLength?: number;
  placeholder?: string;
  onChange: (value: string) => void;
};

export const TemplateInput = defineComponent<TemplateInputProps>({
  name: "StudioTemplateInput",
  props: ["value", "channel", "maxLength", "placeholder", "onChange"],
  setup(props) {
    const el = ref<HTMLInputElement>();
    const focused = ref(false);
    return () => (
      <div class="space-y-2">
        <input
          ref={el}
          type="text"
          class="input input-bordered input-sm w-full focus:input-primary"
          maxlength={props.maxLength}
          placeholder={props.placeholder}
          value={props.value}
          onFocus={() => (focused.value = true)}
          onBlur={() => (focused.value = false)}
          onInput={(e) => props.onChange((e.target as HTMLInputElement).value)}
        />
        {focused.value && (
          <VariableChips
            channel={props.channel}
            onInsert={(token) => props.onChange(insertAtCursor(el.value, props.value, token))}
          />
        )}
      </div>
    );
  },
});

// ----------------------------
// Device frame used by WhatsApp / SMS / push previews
// ----------------------------

type PhoneFrameProps = { background?: string };

export const PhoneFrame = defineComponent<PhoneFrameProps>({
  name: "StudioPhoneFrame",
  props: ["background"],
  setup(props, { slots }) {
    return () => (
      <div class="mx-auto w-[300px] shrink-0 rounded-[2.6rem] border-[10px] border-neutral bg-neutral shadow-xl">
        <div
          class="relative flex h-[600px] flex-col overflow-hidden rounded-[2rem]"
          style={{ background: props.background ?? "oklch(var(--b1))" }}
        >
          <div class="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-neutral" />
          <div class="flex h-9 shrink-0 items-end justify-between px-6 pb-1 text-[11px] font-semibold text-neutral-content mix-blend-difference">
            <span>9:41</span>
            <span class="flex gap-1">
              <span class="material-symbols-outlined text-[13px]">signal_cellular_alt</span>
              <span class="material-symbols-outlined text-[13px]">battery_full</span>
            </span>
          </div>
          {slots.default?.()}
        </div>
      </div>
    );
  },
});

export const CharCounter = (used: number, max: number) => `${used} / ${max}`;
